import type { PropostaItem, ComboVariation, ComboVariationWithPricing } from '@/types'
import { formatCategoryDisplayTitle, normalizeTextEncoding } from '@/utils/text-normalization'

export type TemplateType = 'cro' | 'layout-personalizado' | 'ecommerce' | 'site-institucional' | 'generico'

export interface PlanServiceItem {
  id: string
  nome: string
  valor: number
  categoria?: string
  recorrente?: boolean
  duracao?: number
  isMKT?: boolean
  comboFinalPrice?: number
  description?: string
  descricao?: string
  isIncluded?: boolean
  isPartial?: boolean
  quantity?: string | number
  quantidade?: string | number
}

export interface DealCalculationResult {
  valorTotal: number
  valorMensal: number
  valorSetup: number
  valorDesconto: number
  tcv: number
  subtotal: number
}

export interface PlanData {
  id: string
  nome: string
  descricao?: string
  tipo?: string
  templateType?: TemplateType
  servicos: PlanServiceItem[]
  pricing: {
    valor: number
    valorMensal?: number
    valorSetup?: number
    desconto?: number
    total?: number
    duracao?: number
    recorrente?: boolean
  }
  validade?: number
  observacoes?: string
  showBonus?: boolean
  bonusText?: string
  paymentDetails?: string
  loyaltyDuration?: string
  /** Data limite da negociação (ISO YYYY-MM-DD). */
  negotiationValidUntil?: string
  /** Horas de suporte/mês para informativo no slide (planos Suporte). */
  supportHours?: number
  /** Faixas horas/valor mensal (planos Suporte multi-opção). */
  supportTiers?: Array<{ supportHours: number; monthlyValue: number }>
  layoutConfig?: LayoutConfig
  /** Rótulo do bloco 3 (ex.: Suporte, Plano de Desenvolvimento). */
  projectType?: string
  /** Tipo comercial do item: plano | servico | avulso. */
  itemTipo?: string
  /** Valor mensal original (de) para exibição de/por em combo. */
  priceCompareFrom?: number
}

export interface LayoutConfig {
  leftBlockWidth: number
  dividerX: number
  titleY: number
  projectTypeY: number
  descriptionY: number
  /** POS Y do informativo de horas de suporte (abaixo da descrição). */
  supportHoursY?: number
  bonusY: number
  priceY: number
  paymentY: number
  loyaltyY: number
  /** POS Y do aviso de validade da negociação (abaixo da fidelidade). */
  negotiationY?: number
  planTitleY: number
  serviceBlockStartY: number
  serviceBlockX: number
  serviceBlockGap: number
  categoryCoords?: Record<string, { x: number; y: number }>
  planName?: string
  paymentDetails?: string
  loyaltyDuration?: string
  description?: string
  bonusText?: string
}

/** POS Y padrão dos blocos no bloco verde (esquerda). */
export const DEFAULT_TITLE_Y = 7.1
export const DEFAULT_PROJECT_TYPE_Y = 16.4
export const DEFAULT_DESCRIPTION_Y = 26.5
export const DEFAULT_SUPPORT_HOURS_Y = 47
export const DEFAULT_BONUS_Y = 60.4
export const DEFAULT_PRICE_Y = 75.3
export const DEFAULT_PAYMENT_Y = 84.5
export const DEFAULT_LOYALTY_Y = 89.3
export const DEFAULT_NEGOTIATION_Y = 93.2

/** Converte POS Y do layout (0–100 %) em fração 0–1 da altura do slide. */
export function layoutPercentToFraction(
  value: number | undefined,
  fallbackPercent: number,
): number {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return fallbackPercent / 100
  }
  if (value > 1) return value / 100
  return value
}

export const defaultLayoutConfig: LayoutConfig = {
  leftBlockWidth: 35,
  dividerX: 35,
  titleY: DEFAULT_TITLE_Y,
  projectTypeY: DEFAULT_PROJECT_TYPE_Y,
  descriptionY: DEFAULT_DESCRIPTION_Y,
  supportHoursY: DEFAULT_SUPPORT_HOURS_Y,
  bonusY: DEFAULT_BONUS_Y,
  priceY: DEFAULT_PRICE_Y,
  paymentY: DEFAULT_PAYMENT_Y,
  loyaltyY: DEFAULT_LOYALTY_Y,
  negotiationY: DEFAULT_NEGOTIATION_Y,
  planTitleY: 2.7,
  serviceBlockStartY: 10,
  serviceBlockX: 38,
  serviceBlockGap: 68,
  categoryCoords: {},
}

const MKT_KEYWORDS = ['tráfego', 'trafego', 'ads', 'anúncio', 'anuncio', 'mídia', 'midia', 'google ads', 'meta ads']

function isMKTItem(item: { nome?: string; name?: string; descricao?: string; description?: string }): boolean {
  const searchText = [item.nome, item.name, item.descricao, item.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return MKT_KEYWORDS.some((kw) => searchText.includes(kw))
}

export function calculateDealTotal(
  items: PropostaItem[],
  desconto: number = 0
): DealCalculationResult {
  const mktItems = items.filter((i) => isMKTItem(i))
  const naoMKTItems = items.filter((i) => !isMKTItem(i))

  const somaNaoMKT = naoMKTItems.reduce((acc, item) => acc + item.total, 0)

  // MKT exclusivo: só o de menor valor total entra no cálculo
  let somaMKT = 0
  let menorMKTItem: PropostaItem | null = null
  if (mktItems.length > 0) {
    menorMKTItem = mktItems.reduce((min, item) => (item.total < min.total ? item : min))
    somaMKT = menorMKTItem.total
  }

  const subtotal = somaNaoMKT + somaMKT
  const valorDesconto = subtotal * (desconto / 100)
  const valorTotal = subtotal - valorDesconto

  // Para o breakdown setup/mensal, considera todos os não-MKT + só o menor MKT
  const allValidItems = menorMKTItem ? [...naoMKTItems, menorMKTItem] : naoMKTItems
  const setupItems = allValidItems.filter((i) => i.tipo === 'avulso')
  const recurringItems = allValidItems.filter((i) => i.tipo === 'servico' || i.tipo === 'plano')

  const valorSetup = setupItems.reduce((acc, item) => acc + item.total, 0)
  const valorMensal = recurringItems.reduce((acc, item) => acc + item.total, 0)

  return {
    valorTotal,
    valorMensal,
    valorSetup,
    valorDesconto,
    tcv: valorTotal,
    subtotal,
  }
}

export function calculateMKTExclusivo(
  servicos: PlanServiceItem[]
): { valorMKT: number; valorNaoMKT: number; total: number } {
  const mktItems = servicos.filter((s) => s.isMKT)
  const naoMKTItems = servicos.filter((s) => !s.isMKT)

  const valorNaoMKT = naoMKTItems.reduce((acc, s) => {
    const price = s.comboFinalPrice ?? s.valor
    return acc + price
  }, 0)

  let valorMKT = 0
  if (mktItems.length > 0) {
    const menorMKT = mktItems.reduce((min, s) => {
      const price = s.comboFinalPrice ?? s.valor
      return price < min ? price : min
    }, Infinity)
    valorMKT = menorMKT
  }

  return {
    valorMKT,
    valorNaoMKT,
    total: valorMKT + valorNaoMKT,
  }
}

export function calculateTCV(plan: PlanData): number {
  if (plan.pricing.total) return plan.pricing.total
  if (plan.pricing.recorrente && plan.pricing.duracao) {
    return plan.pricing.valor * plan.pricing.duracao
  }
  return plan.pricing.valor
}

export function groupServicesByCategory(
  servicos: PlanServiceItem[]
): Map<string, PlanServiceItem[]> {
  const groups = new Map<string, PlanServiceItem[]>()
  for (const s of servicos) {
    const cat = s.categoria || 'Outros'
    if (!groups.has(cat)) groups.set(cat, [])
    groups.get(cat)!.push(s)
  }
  return groups
}

export function extractServices(p: any): any[] {
  if (!p) return []
  const cfg = p.config || {}

  // 1. Prioridade: services aninhados (Estrutura completa com categorias)
  const mapCategory = (cat: any) => ({
    category: formatCategoryDisplayTitle(cat.category || cat.categoria || 'Geral'),
    services: (cat.services || cat.servicos || []).map((svc: any) => ({
      name: normalizeTextEncoding(svc.name || svc.nome || ''),
      isIncluded: svc.isIncluded !== undefined ? !!svc.isIncluded : true,
      isPartial: svc.isPartial !== undefined ? !!svc.isPartial : false,
      quantity: String(svc.quantity || svc.quantidade || ''),
    })),
  })

  const buildFromDeliverables = () => {
    const groups: Record<string, any[]> = {}
    cfg.deliverables.forEach((d: any) => {
      const name = normalizeTextEncoding(d.name || d.nome || '').trim()
      if (!name) return
      const catName = formatCategoryDisplayTitle(d.category || d.categoria || p.category || 'Geral')
      if (!groups[catName]) groups[catName] = []
      groups[catName].push({
        name,
        isIncluded: d.isIncluded !== undefined ? !!d.isIncluded : true,
        isPartial: d.isPartial !== undefined ? !!d.isPartial : false,
        quantity: String(d.quantity || d.quantidade || '1'),
      })
    })
    return Object.keys(groups).map((catName) => ({
      category: catName,
      services: groups[catName],
    }))
  }

  const nestedServices =
    Array.isArray(cfg.services) && cfg.services.length > 0
      ? cfg.services.map(mapCategory)
      : Array.isArray(p.services) && p.services.length > 0
        ? p.services.map(mapCategory)
        : []

  // 1. Fonte oficial: deliverables — se vier tudo num bucket genérico, usa services aninhado (3 grupos)
  if (Array.isArray(cfg.deliverables) && cfg.deliverables.length > 0) {
    const fromDeliverables = buildFromDeliverables()
    const deliverableItems = fromDeliverables.reduce(
      (sum, cat) => sum + (cat.services?.length || 0),
      0,
    )
    const singleGenericBucket =
      fromDeliverables.length === 1 &&
      deliverableItems >= 6 &&
      ['marketing', 'recorrente', 'geral'].includes(
        categoryMatchKey(fromDeliverables[0].category || ''),
      )

    if (singleGenericBucket && nestedServices.length >= 2) {
      return nestedServices
    }
    return fromDeliverables
  }

  // 2. Legado: estrutura aninhada services (propostas antigas ou imports)
  if (nestedServices.length > 0) {
    return nestedServices
  }

  return []
}

export function categoryMatchKey(value: string): string {
  return normalizeTextEncoding(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

/** Mescla entregáveis da matriz do plano (todos incluídos) com itens extras do template da categoria. */
export function findCategoryCoords(
  categoryCoords: Record<string, { x: number; y: number }> | undefined,
  categoria: string,
): { x: number; y: number } | undefined {
  if (!categoryCoords) return undefined
  const target = categoryMatchKey(categoria)
  for (const [key, val] of Object.entries(categoryCoords)) {
    if (categoryMatchKey(key) === target) return val
  }
  return undefined
}

export function mergePlanDeliverablesWithTemplate(planServices: any[], template: any[] = []): any[] {
  if (!planServices.length) {
    return template.length ? JSON.parse(JSON.stringify(template)) : []
  }

  const result = JSON.parse(JSON.stringify(planServices))

  for (const tplCat of template) {
    const tplCategory = tplCat.category || tplCat.categoria || 'Geral'
    let destCat = result.find(
      (c: any) => categoryMatchKey(c.category || c.categoria) === categoryMatchKey(tplCategory),
    )

    if (!destCat) {
      result.push({
        category: tplCategory,
        services: (tplCat.services || []).map((s: any) => ({
          name: s.name || s.nome || '',
          isIncluded: false,
          isPartial: !!s.isPartial,
          quantity: String(s.quantity || s.quantidade || ''),
        })),
      })
      continue
    }

    const destServices = destCat.services || []
    for (const tplSvc of tplCat.services || []) {
      const tplName = tplSvc.name || tplSvc.nome || ''
      const exists = destServices.some(
        (s: any) => categoryMatchKey(s.name || s.nome) === categoryMatchKey(tplName),
      )
      if (!exists) {
        destServices.push({
          name: tplName,
          isIncluded: false,
          isPartial: !!tplSvc.isPartial,
          quantity: String(tplSvc.quantity || tplSvc.quantidade || ''),
        })
      }
    }
    destCat.services = destServices
  }

  return result
}

export interface MergedPlan {
  id: string
  nome: string
  descricao: string
  tipo: string
  servicos: PlanServiceItem[]
  pricing: {
    valor: number
    valorMensal: number
    recorrente: boolean
    duracao: number
  }
}

function getPlanDisplayName(plan: any): string {
  return (plan?.nome || plan?.name || plan?.id || 'Servico').trim()
}

function normalizeMergedGroups(plan: any): { category: string; services: Array<{ name: string; quantity?: string | null; isIncluded?: boolean; description?: string }> }[] {
  if (!plan) return []

  const cfg = plan.config || {}

  const asGrouped = (value: any): { category: string; services: Array<{ name: string; quantity?: string | null; isIncluded?: boolean; description?: string }> }[] => {
    if (!Array.isArray(value)) return []
    return value
      .map((entry) => {
        const category = entry?.category || entry?.categoria || 'Geral'
        const services = Array.isArray(entry?.services) ? entry.services : Array.isArray(entry?.servicos) ? entry.servicos : []
        return {
          category,
          services: services
            .map((svc: any) => ({
              name: (svc?.name || svc?.nome || svc?.label || '').trim(),
              quantity: svc?.quantity || svc?.quantidade || null,
              isIncluded: svc?.isIncluded !== false,
              description: svc?.description || svc?.descricao || '',
            }))
            .filter((svc: { name: string }) => svc.name),
        }
      })
      .filter((entry) => entry.services.length > 0)
  }

  if (Array.isArray(plan.services) && plan.services.length > 0) {
    const first = plan.services[0]
    if (first && (Array.isArray(first.services) || Array.isArray(first.servicos))) {
      return asGrouped(plan.services)
    }
  }

  if (Array.isArray(cfg.services) && cfg.services.length > 0) {
    return asGrouped(cfg.services)
  }

  if (Array.isArray(cfg.deliverables) && cfg.deliverables.length > 0) {
    const groups = new Map<string, Array<{ name: string; quantity?: string | null; isIncluded?: boolean; description?: string }>>()
    for (const deliverable of cfg.deliverables) {
      const category = deliverable?.category || deliverable?.categoria || plan?.category || 'Geral'
      if (!groups.has(category)) groups.set(category, [])
      const name = (deliverable?.name || deliverable?.nome || deliverable?.label || '').trim()
      if (!name) continue
      groups.get(category)!.push({
        name,
        quantity: deliverable?.quantity || deliverable?.quantidade || null,
        isIncluded: deliverable?.isIncluded !== false,
        description: deliverable?.description || deliverable?.descricao || '',
      })
    }
    return Array.from(groups.entries()).map(([category, services]) => ({ category, services }))
  }

  const flatItems = Array.isArray(plan.items) && plan.items.length > 0
    ? plan.items
    : Array.isArray(cfg.items) && cfg.items.length > 0
      ? cfg.items
      : Array.isArray(plan.servicos) && plan.servicos.length > 0
        ? plan.servicos
        : []

  if (flatItems.length > 0) {
    const groups = new Map<string, Array<{ name: string; quantity?: string | null; isIncluded?: boolean; description?: string }>>()
    for (const item of flatItems) {
      const category = item?.categoria || item?.category || plan?.category || 'Geral'
      if (!groups.has(category)) groups.set(category, [])
      const name = (item?.name || item?.nome || item?.descricao || item?.description || '').trim()
      if (!name) continue
      groups.get(category)!.push({
        name,
        quantity: item?.quantity || item?.quantidade || null,
        isIncluded: item?.isIncluded !== false,
        description: item?.description || item?.descricao || '',
      })
    }
    return Array.from(groups.entries()).map(([category, services]) => ({ category, services }))
  }

  return []
}

export function mergePlans(plans: PlanData[]): MergedPlan {
  const mergedCategories = new Map<string, { category: string; services: PlanServiceItem[] }>()
  let totalValor = 0
  let totalMensal = 0
  let isRecorrente = false
  let maxDuracao = 0

  for (const plan of plans) {
    const planName = getPlanDisplayName(plan)
    const categories = normalizeMergedGroups(plan)

    for (const category of categories) {
      const prefixedCategory = `${planName} ::: ${category.category || 'Geral'}`
      if (!mergedCategories.has(prefixedCategory)) {
        mergedCategories.set(prefixedCategory, { category: prefixedCategory, services: [] })
      }

      const bucket = mergedCategories.get(prefixedCategory)!
      for (const svc of category.services) {
        const svcName = (svc.name || '').trim()
        if (!svcName) continue
        const exists = bucket.services.find((existing) => existing.nome.toLowerCase().trim() === svcName.toLowerCase().trim())
        if (exists) continue
        bucket.services.push({
          id: `${plan.id || planName}-${bucket.category}-${svcName}`.replace(/\s+/g, '-').toLowerCase(),
          nome: svcName,
          valor: Number((svc as any).valor || plan.pricing?.valor || 0),
          categoria: bucket.category,
          recorrente: plan.pricing?.recorrente,
          duracao: plan.pricing?.duracao,
          description: svc.description,
          quantity: svc.quantity ?? undefined,
          isIncluded: svc.isIncluded,
        } as PlanServiceItem)
      }
    }

    totalValor += Number(plan.pricing.total ?? plan.pricing.valor ?? 0)
    totalMensal += Number(plan.pricing.valorMensal ?? plan.pricing.valor ?? 0)
    if (plan.pricing.recorrente) isRecorrente = true
    if ((plan.pricing.duracao || 0) > maxDuracao) maxDuracao = plan.pricing.duracao || 0
  }

  return {
    id: `merged_${Date.now()}`,
    nome: `Combo Estratégico — ${plans.map((p) => p.nome).join(' + ')}`,
    descricao: `Pacote combinado de serviços: ${plans.map((p) => p.nome).join(', ')}.`,
    tipo: isRecorrente ? 'plano' : 'servico',
    servicos: Array.from(mergedCategories.values()).flatMap((group) => group.services.map((svc) => ({
      ...svc,
      categoria: group.category,
    }))),
    pricing: {
      valor: totalValor,
      valorMensal: totalMensal,
      recorrente: isRecorrente,
      duracao: maxDuracao || 12,
    },
  }
}

// ─── Variações de Combo (Propostas Multi-Opção) ─────────────────────────────

/** Valor mensal recorrente do item (não usa TCV em item.total). */
export function getItemMonthlyValue(item: PropostaItem): number {
  if (item.tipo !== 'plano' && item.tipo !== 'servico') return 0
  const qty = item.quantidade || 1
  const pricing = item.pricing
  if (pricing?.installmentValue && pricing.installmentValue > 0) {
    return pricing.installmentValue * qty
  }
  if (pricing?.recorrente && (item.total || 0) > 0) {
    const months = pricing.installments || item.duration || 12
    const entry = pricing.entry || 0
    if (months > 0) return Math.max(0, ((item.total || 0) - entry) / months)
  }
  return (item.valorUnitario || 0) * qty
}

export function getComboDurationFromItems(items: PropostaItem[]): number {
  const durations = items
    .filter((i) => i.tipo === 'plano' || i.tipo === 'servico')
    .map((i) => i.duration || i.pricing?.installments || i.pricing?.duracao || 12)
  return durations.length > 0 ? Math.max(...durations) : 12
}

export function calculateVariationPricing(
  variation: ComboVariation,
  allItems: PropostaItem[]
): ComboVariationWithPricing {
  const items = allItems.filter((i) => variation.itemIds.includes(i.id))
  const valorOriginalMensal = items.reduce((acc, i) => acc + getItemMonthlyValue(i), 0)

  let valorFinalMensal = valorOriginalMensal
  if (variation.precoFechado !== undefined && variation.precoFechado !== null) {
    valorFinalMensal = variation.precoFechado
  } else if (variation.descontoPercentual && variation.descontoPercentual > 0) {
    valorFinalMensal = valorOriginalMensal * (1 - variation.descontoPercentual / 100)
  }

  const valorSetup = items
    .filter((i) => i.tipo === 'avulso')
    .reduce((acc, i) => acc + (i.total || 0), 0)

  return {
    ...variation,
    items,
    valorOriginal: valorOriginalMensal,
    valorFinal: valorFinalMensal,
    valorMensal: valorFinalMensal,
    valorSetup,
  }
}

export function calculateAllVariations(
  variations: ComboVariation[],
  allItems: PropostaItem[]
): ComboVariationWithPricing[] {
  return variations.map((v) => calculateVariationPricing(v, allItems))
}
